/**
 * API base URL for outbound HTTP calls.
 *
 * - Browser: always NEXT_PUBLIC_BASE_URL (public ALB / user-api-gw).
 * - Server: prod pins to callback unless INTERNAL_API_URL is the internal GW
 *   (hzc-gw-int). Do not treat every *.hizlicicek.online host as "dev" —
 *   that would also match prod internal.
 * - hzc-dev: INTERNAL_API_URL (dev-user-api-gw) wins; if it is missing, a
 *   baked NEXT_PUBLIC_BASE_URL on a dev-* .online host is used so INIT does
 *   not silently hit callback.hizlicicek.com.
 */
const PROD_USER_API_GW = 'https://callback.hizlicicek.com';
const PROD_INTERNAL_GW_HOST = 'hzc-gw-int.hizlicicek.online';

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function isLoopback(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1';
}

/** hzc-dev / local only — not prod internal (hzc-gw-int). */
function isDevApiUrl(url: string): boolean {
  const host = hostnameOf(url);
  if (!host) return /localhost|127\.0\.0\.1/i.test(url);
  if (isLoopback(host)) return true;
  return host.startsWith('dev-') && host.endsWith('.hizlicicek.online');
}

function isProdInternalGw(url: string): boolean {
  return hostnameOf(url) === PROD_INTERNAL_GW_HOST;
}

export function getApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    const fromEnv =
      process.env.INTERNAL_API_URL?.trim() || process.env.USER_API_GW_URL?.trim() || '';
    if (fromEnv && (isDevApiUrl(fromEnv) || isProdInternalGw(fromEnv))) {
      return fromEnv;
    }
    const publicUrl = process.env.NEXT_PUBLIC_BASE_URL?.trim() || '';
    if (publicUrl && isDevApiUrl(publicUrl)) {
      return publicUrl;
    }
    return PROD_USER_API_GW;
  }
  return process.env.NEXT_PUBLIC_BASE_URL?.trim() || '';
}
